/**
 * Pill — generic small label/tag with semantic tones.
 *
 * Single responsibility: render a compact rounded label with consistent
 * typography. Tones map to the CSK design tokens; arbitrary colors are
 * also supported for theorem-keyed accents.
 *
 * Tone-driven foreground / background still flow through inline `style`
 * so consumers that pass arbitrary `color` / `bg` overrides keep working.
 * Layout, typography, rounding, and gap are driven by Tailwind utilities
 * so consumers can extend or override per call site via `className`
 * (resolved through `cn()` for predictable conflict semantics).
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { GRAY, PRIMARY, SAFE, WARN } from "../design-system/tokens.js";
import { cn } from "../styling/index.js";

/**
 * Semantic tone vocabulary.
 *
 * - `warning` is the standard cautionary treatment (light red wash on a
 *   strong red foreground).
 * - `danger` is reserved for the more severe variant — same red foreground
 *   on a noticeably stronger background tint, mirroring the `warn`/`danger`
 *   distinction in `StatusBadge`. Use `danger` for receipts that name an
 *   uninhabitable categorical shape (forbidden crossing, witness forgery);
 *   use `warning` for soft-cautionary content that does not refute a claim.
 */
export type PillTone = "neutral" | "info" | "success" | "warning" | "danger" | "accent";

export type PillSize = "sm" | "md";

export interface PillProps {
  /** Pill content. */
  readonly children: React.ReactNode;
  /** Semantic tone — picks a token-aligned color pair. */
  readonly tone?: PillTone;
  /** Override foreground color. Takes precedence over `tone`. */
  readonly color?: string;
  /** Override background color. Takes precedence over `tone`. */
  readonly bg?: string;
  /** Optional 1px solid border color. Adds a hairline outline matching the tone. */
  readonly border?: string;
  /** Use monospace font. */
  readonly mono?: boolean;
  /** Size; defaults to "sm". */
  readonly size?: PillSize;
  /** Optional leading icon (emoji or small node). */
  readonly icon?: React.ReactNode;
  /** Optional title (native tooltip). */
  readonly title?: string;
  /**
   * Optional inline style overrides merged after the computed defaults.
   * Useful for status-pill variants that need uppercase + letter-spacing.
   */
  readonly style?: React.CSSProperties;
  /**
   * Additional Tailwind class names. Resolved via `cn()`, so later
   * conflicting utilities (`px-4`, `text-lg`, ...) override the defaults.
   */
  readonly className?: string;
}

interface ToneStyle {
  readonly color: string;
  readonly bg: string;
}

const TONE_STYLE: Readonly<Record<PillTone, ToneStyle>> = {
  neutral: { color: GRAY[700], bg: GRAY[100] },
  info: { color: PRIMARY[700], bg: PRIMARY[100] },
  success: { color: SAFE[700], bg: SAFE[100] },
  warning: { color: WARN[700], bg: WARN[100] },
  // `danger` shares `warning`'s foreground but uses a noticeably stronger
  // background tint (WARN[300] vs WARN[100]) so the two tones are visually
  // distinct. Aligned with StatusBadge's `warn`/`danger` palette.
  danger: { color: WARN[700], bg: WARN[300] },
  accent: { color: PRIMARY[800], bg: PRIMARY[100] },
};

interface SizeSpec {
  /** Tailwind utility classes for padding and font sizing. */
  readonly className: string;
  /**
   * Inline font-size mirror so consumers (and unit tests) reading
   * `element.style.fontSize` see the same value Tailwind produces.
   * Kept in sync via the spec — bumping the class without bumping the
   * inline value is structurally impossible.
   */
  readonly fontSize: string;
}

const SIZE: Readonly<Record<PillSize, SizeSpec>> = {
  sm: { className: "px-2 py-0.5 text-[0.6875rem]", fontSize: "0.6875rem" },
  md: { className: "px-2.5 py-1 text-[0.8125rem]", fontSize: "0.8125rem" },
};

/** Renders a compact rounded label with consistent typography. */
export const Pill = React.memo(function Pill({
  children,
  tone = "neutral",
  color,
  bg,
  border,
  mono = false,
  size = "sm",
  icon,
  title,
  style,
  className,
}: PillProps): React.JSX.Element {
  const tokens = TONE_STYLE[tone];
  const fg = color ?? tokens.color;
  const background = bg ?? tokens.bg;
  const inlineBorder = border !== undefined ? { border: `1px solid ${border}` } : {};
  const sizing = SIZE[size];
  return (
    <span
      title={title}
      className={cn(
        "inline-flex items-center whitespace-nowrap rounded-full font-semibold",
        mono ? "font-mono" : "font-body",
        sizing.className,
        className,
      )}
      style={{
        color: fg,
        backgroundColor: background,
        fontSize: sizing.fontSize,
        gap: icon !== undefined ? "0.3rem" : 0,
        ...inlineBorder,
        ...style,
      }}
    >
      {icon !== undefined && <span aria-hidden="true">{icon}</span>}
      <span>{children}</span>
    </span>
  );
});
